import { useState, useMemo } from 'react';
import {
  AlertBanner,
  Badge,
  BodyText,
  Button,
  ButtonGroup,
  Card,
  DataTable,
  DateRangePicker,
  EmptyState,
  Heading,
  Select,
  Skeleton,
  Switch,
  formatDateTime,
} from '@aaimplatform/ui';
import type { DataTableColumn, DateRange, SelectOption } from '@aaimplatform/ui';
import {
  Archive as ArchiveIcon,
  Download as DownloadIcon,
  FileSearch as FileSearchIcon,
  AlertTriangle as WarningIcon,
} from 'lucide-react';
import {
  usePreviewManifest,
  useDownloadEvidencePackage,
  EvidencePackageError,
} from '../../hooks/useExaminerEvidence';
import type {
  EvidencePackageRequest,
  EvidenceArtifactEntry,
  EvidenceExternalReference,
} from '../../hooks/useExaminerEvidence';
import { useAuthStore } from '../../stores/auth';
import { PLATFORM_ADMIN_ROLES } from '../../lib/platform-admin-roles';

/**
 * Roles the evidence-package endpoints admit: the platform-admin set, the
 * examiner role, and FI tenant staff holding fi_admin or fi_compliance
 * (platform-admin-api examiner-role.middleware.ts requireEvidencePackageAccess
 * and EVIDENCE_PACKAGE_FI_ROLES; operator ruling 2026-10-21, confirmed
 * 2026-10-23: "Examiner evidence is for tenants to prove compliance"). The
 * server binds FI staff to their own tenant claim. Mirrors the nav gates on
 * this item in PortalLayout.tsx. Compared lowercase on both sides
 * (.claude/rules/authz-case-insensitive.md).
 */
const EVIDENCE_PACKAGE_ROLES: readonly string[] = [
  ...PLATFORM_ADMIN_ROLES,
  'examiner',
  'fi_admin',
  'fi_compliance',
];

/**
 * Pledge-status whitelist options for the scope filter. Values mirror the
 * PledgeStatus enum in schema-registry/collateral-pledge.openapi.yaml; the
 * server intersects the selection with any examiner scope grant.
 */
const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ACKNOWLEDGED', label: 'Acknowledged' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'RELEASED', label: 'Released' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'WITHDRAWN', label: 'Withdrawn' },
  { value: 'DECLINED', label: 'Declined' },
  { value: 'RETURNED', label: 'Returned' },
];

/** Render a byte count as a human-scaled string (integer bytes from the spec). */
function formatBytes(sizeBytes: number): string {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Truncate a SHA-256 hex digest for table display; full value in title. */
function shortHash(sha256: string): string {
  return sha256.length > 16 ? `${sha256.slice(0, 16)}...` : sha256;
}

const ARTIFACT_COLUMNS: DataTableColumn<EvidenceArtifactEntry>[] = [
  { header: 'Category', accessor: 'category' },
  { header: 'Filename', accessor: 'filename', monospace: true },
  {
    header: 'Size',
    accessor: 'sizeBytes',
    align: 'right',
    cell: (value) => formatBytes(value as number),
  },
  {
    header: 'SHA-256',
    accessor: 'sha256',
    monospace: true,
    cell: (value) => <span title={value as string}>{shortHash(value as string)}</span>,
  },
];

const EXTERNAL_REFERENCE_COLUMNS: DataTableColumn<EvidenceExternalReference>[] = [
  { header: 'Category', accessor: 'category' },
  { header: 'Description', accessor: 'description' },
  { header: 'Source', accessor: 'source', monospace: true },
  { header: 'Endpoint', accessor: 'endpoint', monospace: true },
];

/**
 * Examiner Evidence page -- /audit/examiner-evidence (GH #1930).
 *
 * Scope-bounded evidence package for a regulatory examiner: preview the
 * integrity manifest (per-artifact hashes + reproducible package hash), then
 * download the ZIP. Served by platform-admin-api at
 * /api/admin/examiners/evidence-package[/manifest]; FI staff (fi_admin,
 * fi_compliance) and platform admins resolve the tenant from their own claim,
 * examiners from their examinerScope claim (the request window is intersected
 * server-side with the grant). The page sends no fiId: the server refuses one
 * from anyone but a platform admin.
 *
 * Fail-closed posture: when core evidence (UCC filings) is configured but the
 * upstream fetch fails, the server returns 502 CORE_EVIDENCE_UNAVAILABLE and
 * produces NO manifest or ZIP -- the page surfaces that as a distinct banner
 * rather than a generic failure.
 */
export function ExaminerEvidencePage() {
  const user = useAuthStore((s) => s.user);
  const canAssemble = (user?.roles ?? []).some(
    (role) => typeof role === 'string' && EVIDENCE_PACKAGE_ROLES.includes(role.toLowerCase()),
  );

  // The route is reachable by URL for every persona, but both endpoints
  // answer 403 to anyone outside the role set. Say so instead of rendering
  // two actions that can only fail.
  if (!canAssemble) {
    return (
      <EmptyState
        icon={<ArchiveIcon className="h-8 w-8" />}
        title="Evidence packages require administrator or compliance access"
        description="Your institution's administrators and compliance staff assemble evidence packages, as do examiners with an active scope grant and Aaim support. Your role does not include this access."
      />
    );
  }

  return <ExaminerEvidenceWorkspace />;
}

function ExaminerEvidenceWorkspace() {
  const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [includePledges, setIncludePledges] = useState(true);
  const [includeContracts, setIncludeContracts] = useState(true);

  const previewManifest = usePreviewManifest();
  const downloadPackage = useDownloadEvidencePackage();

  const request: EvidencePackageRequest = useMemo(() => {
    const body: EvidencePackageRequest = { includePledges, includeContracts };
    if (dateRange.start) {
      body.since = dateRange.start.toISOString();
    }
    if (dateRange.end) {
      // Include the whole selected end day: the picker yields start-of-day
      // dates while the spec bound is a date-time upper limit on createdAt.
      const endOfDay = new Date(dateRange.end);
      endOfDay.setHours(23, 59, 59, 999);
      body.until = endOfDay.toISOString();
    }
    if (statusFilter !== 'ALL') {
      body.statusFilter = [statusFilter];
    }
    return body;
  }, [dateRange, statusFilter, includePledges, includeContracts]);

  const manifest = previewManifest.data;
  const activeError = previewManifest.error ?? downloadPackage.error;
  const isCoreEvidenceUnavailable =
    activeError instanceof EvidencePackageError && activeError.isCoreEvidenceUnavailable;
  const isBusy = previewManifest.isPending || downloadPackage.isPending;

  return (
    <div>
      {/* Header -- page title lives in the sticky topnav (AppHeader); the body
          opens with the descriptive subtitle, no redundant H1. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <p className="text-sm font-sans text-tertiary">
          Assemble a scope-bounded, tamper-evident evidence package for a regulatory
          examiner: pledge records, executed contracts, and signature audit trails with a
          reproducible integrity manifest.
        </p>
        <ButtonGroup equalWidth>
          <Button
            variant="secondary"
            onClick={() => previewManifest.mutate(request)}
            disabled={isBusy}
          >
            <FileSearchIcon size={20} />
            Preview manifest
          </Button>
          <Button
            variant="primary"
            onClick={() => downloadPackage.mutate(request)}
            disabled={isBusy}
          >
            <DownloadIcon size={20} />
            Download ZIP
          </Button>
        </ButtonGroup>
      </div>

      {/* Scope controls */}
      <Card className="p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DateRangePicker
            label="Pledge creation window"
            value={dateRange}
            onChange={setDateRange}
            helperText="Limits the package to pledges created in this window. An examiner's access window further limits it."
          />
          <Select
            label="Pledge status filter"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-6 mt-4">
          <Switch
            label="Include pledge records"
            description="Scope-bounded pledge CSV"
            isSelected={includePledges}
            onChange={setIncludePledges}
          />
          <Switch
            label="Include contracts"
            description="Executed contracts + signature audit trail"
            isSelected={includeContracts}
            onChange={setIncludeContracts}
          />
        </div>
      </Card>

      {/* Fail-closed 502: core evidence configured but unavailable upstream. */}
      {isCoreEvidenceUnavailable && (
        <div className="mb-6">
          <AlertBanner
            type="error"
            message="Core evidence (UCC filings) is configured but currently unavailable upstream. The package fails closed: no manifest or ZIP was produced. Retry once the UCC filing service is reachable."
          />
        </div>
      )}

      {/* Generic error state (non-fail-closed failures) */}
      {activeError && !isCoreEvidenceUnavailable && (
        <Card className="p-6 text-center mb-6">
          <WarningIcon className="text-error mb-2 mx-auto" size={32} />
          <BodyText variant="ui" className="text-error mb-2">
            {previewManifest.error ? 'Manifest preview failed' : 'Evidence package download failed'}
          </BodyText>
          <BodyText variant="ui" className="text-tertiary text-sm">
            {activeError.message}
          </BodyText>
        </Card>
      )}

      {/* Loading skeleton while the manifest preview is in flight */}
      {previewManifest.isPending && (
        <Card className="p-6">
          <Skeleton lines={6} />
        </Card>
      )}

      {/* Empty state before the first successful preview */}
      {!manifest && !previewManifest.isPending && !activeError && (
        <EmptyState
          icon={<ArchiveIcon className="h-8 w-8" />}
          title="No manifest previewed yet"
          description="Set the scope above, then preview the manifest to inspect the package contents, per-artifact hashes, and the reproducible package hash before downloading the ZIP."
        />
      )}

      {/* Manifest */}
      {manifest && !previewManifest.isPending && (
        <>
          <Card className="p-4 mb-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <Heading level={2} size="card" className="font-sans mb-1">
                  Package manifest
                </Heading>
                <BodyText variant="ui" className="text-sm text-tertiary font-mono">
                  {manifest.packageId}
                </BodyText>
                <BodyText variant="ui" className="text-xs text-tertiary mt-1">
                  Generated {formatDateTime(manifest.generatedAt)}
                  {manifest.generatedBy ? ` by ${manifest.generatedBy}` : ''}
                </BodyText>
              </div>
              {/* coreBundled is explicit, never derived: true means the UCC
                  filing records were fetched and bundled as artifacts; false
                  means the UCC integration is deliberately unconfigured and
                  the package carries external references only. */}
              {manifest.coreBundled ? (
                <Badge variant="success">UCC records bundled</Badge>
              ) : (
                <Badge variant="info">External references only</Badge>
              )}
            </div>
          </Card>

          <Card className="p-4 mb-6">
            <Heading level={3} size="card" className="font-sans mb-3">
              Artifacts
            </Heading>
            <DataTable<EvidenceArtifactEntry>
              columns={ARTIFACT_COLUMNS}
              data={manifest.artifacts}
              rowKey="filename"
              emptyMessage="No artifacts in scope"
            />
          </Card>

          <Card className="p-4 mb-6">
            <Heading level={3} size="card" className="font-sans mb-3">
              External references
            </Heading>
            <BodyText variant="ui" className="text-xs text-tertiary mb-3">
              Evidence held in another service and referenced, not bundled. An examiner
              fetches these with their own token.
            </BodyText>
            <DataTable<EvidenceExternalReference>
              columns={EXTERNAL_REFERENCE_COLUMNS}
              data={manifest.externalReferences}
              rowKey="endpoint"
              emptyMessage="No external references"
            />
          </Card>

          <Card className="p-4">
            <Heading level={3} size="card" className="font-sans mb-3">
              Integrity
            </Heading>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <BodyText variant="ui" className="text-sm text-tertiary">
                  Algorithm
                </BodyText>
                <BodyText variant="ui" className="font-mono">
                  {manifest.integrity.algorithm}
                </BodyText>
              </div>
              <div>
                <BodyText variant="ui" className="text-sm text-tertiary">
                  Artifact count
                </BodyText>
                <BodyText variant="ui">{manifest.integrity.artifactCount}</BodyText>
              </div>
              <div className="md:col-span-3">
                <BodyText variant="ui" className="text-sm text-tertiary">
                  Package hash
                </BodyText>
                <BodyText variant="ui" className="font-mono break-all">
                  {manifest.integrity.packageHash}
                </BodyText>
                <BodyText variant="ui" className="text-xs text-tertiary mt-1">
                  Reproducible SHA-256 over the sorted (filename, sha256, sizeBytes) tuples
                  plus the scope. Recompute it from the unzipped files to detect tampering.
                </BodyText>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
